import React from 'react';
import { bindActionCreators } from "redux";
import { tokenAction } from "../../redux-stuffs/actions/token_action";
import { connect } from "react-redux";
import { withRouter } from "react-router-dom";
import Header from "../../components/Header";
import api from "../../api/api";
import { accessTokenState } from '../../atoms';
import withRecoil from '../../withRecoil';
import Pagination from '../Pagination';
import './History.css';
import { withTranslation } from 'react-i18next';

class History extends React.Component {
  constructor(props) {
    super(props);
    // Keep internal filter values in English so logic/comparisons stay the same.
    this.state = {
      cashTransactions: [],
      filteredTransactions: [],
      selectedDate: 'All',
      selectedStatus: 'All Statuses',
      selectedType: 'All Types',
      allStatuses: ['All Statuses'],
      allTypes: ['All Types', 'Deposit', 'Withdrawal'],
      currentPage: 1,
      pageSize: 10,
    };
  }

  componentDidMount = async () => {
    await this.combine();
  }

  updateAllStatuses = () => {
    const { cashTransactions } = this.state;
    const uniqueStatuses = ['All Statuses', ...new Set(cashTransactions.map(transaction => transaction.status))];
    this.setState({ allStatuses: uniqueStatuses });
  }

  combine = async () => {
    const transactionsEndpoint = `${api.extra}/api/transaction_record`;

    try {
      const response = await fetch(transactionsEndpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          access_token: this.props.recoilStates.accessTokenState.state
        }),
      });

      const result = await response.json();

      if (response.ok && result.success && Array.isArray(result.data)) {
        const sorted = result.data.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
        this.setState({
          cashTransactions: sorted,
          filteredTransactions: sorted,
          currentPage: 1,
        }, this.updateAllStatuses);
      } else {
        console.error('Failed to fetch transactions:', result.message);
      }
    } catch (err) {
      console.error('Error fetching unified transactions:', err);
    }
  }

  determineColor = (content) => {
    switch (content) {
      case 'DENIED':
      case 'DECLINED':
      case 'FAILED':
      case 'TIMED OUT':
        return 'red';
      case 'COMPLETED':
        return 'green';
      case 'PENDING':
      case 'PIN VERIFIED':
      case 'DISPENSE':
      case 'OPEN REQUEST':
        return 'orange';
      default:
        return 'black';
    }
  };

  determineBack = (content) => {
    switch (content) {
      case 'DENIED':
      case 'DECLINED':
      case 'FAILED':
      case 'TIMED OUT':
        return '#fef3f2';
      case 'COMPLETED':
        return '#ecfdf3';
      case 'PENDING':
      case 'PIN VERIFIED':
      case 'DISPENSE':
      case 'OPEN REQUEST':
        return '#fefebe';
      default:
        return 'light grey';
    }
  };

  // Clear single filters & re-filter
  clearDate = () => this.setState({ selectedDate: 'All' }, this.filterTransactions);
  clearStatus = () => this.setState({ selectedStatus: 'All Statuses' }, this.filterTransactions);
  clearType = () => this.setState({ selectedType: 'All Types' }, this.filterTransactions);

  // Clear all
  clearAllFilters = () => this.setState({
    selectedDate: 'All',
    selectedStatus: 'All Statuses',
    selectedType: 'All Types'
  }, this.filterTransactions);

  filterTransactions = () => {
    const { cashTransactions, selectedDate, selectedStatus, selectedType } = this.state;
    let filtered = [...cashTransactions];

    if (selectedDate !== 'All') {
      filtered = filtered.filter(transaction =>
        new Date(transaction.created_at).toDateString() === selectedDate
      );
    }

    if (selectedStatus !== 'All Statuses') {
      filtered = filtered.filter(transaction =>
        transaction.status === selectedStatus
      );
    }

    if (selectedType !== 'All Types') {
      if (selectedType === 'Withdrawal') {
        filtered = filtered.filter(t => t.source_table === 'transactions');
      } else if (selectedType === 'Deposit') {
        filtered = filtered.filter(t => t.source_table !== 'transactions');
      }
    }

    this.setState({ filteredTransactions: filtered, currentPage: 1 });
  };

  goToPage = (page) => {
    this.setState(
      (prev) => {
        const totalPages = Math.max(1, Math.ceil(prev.filteredTransactions.length / prev.pageSize));
        const next = Math.min(Math.max(1, page), totalPages);
        return { currentPage: next };
      },
      () => {
        try {
          document.querySelector('.transaction-container')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        } catch {}
      }
    );
  };

  render() {
    const { t } = this.props;

    const {
      filteredTransactions, selectedDate, selectedStatus, selectedType,
      cashTransactions, allStatuses, allTypes, currentPage, pageSize
    } = this.state;

    const allDates = [...new Set(cashTransactions.map(t => new Date(t.created_at).toDateString()))];
    const sortedAllDates = allDates.sort((a, b) => new Date(b) - new Date(a));

    const total = filteredTransactions.length;
    const totalPages = Math.max(1, Math.ceil(total / pageSize));
    const startIndex = (currentPage - 1) * pageSize;
    const endIndex = Math.min(startIndex + pageSize, total);
    const pageTransactions = filteredTransactions.slice(startIndex, endIndex);

    const pageTransactionsByDate = pageTransactions.reduce((acc, t) => {
      const date = new Date(t.created_at).toDateString();
      if (!acc[date]) acc[date] = [];
      acc[date].push(t);
      return acc;
    }, {});

    // Helpers to translate labels while keeping filter values intact
    const renderTypeLabel = (typeValue) => {
      if (typeValue === 'All Types') return t('history:filters.types.all');
      if (typeValue === 'Deposit') return t('history:filters.types.deposit');
      if (typeValue === 'Withdrawal') return t('history:filters.types.withdrawal');
      return typeValue;
    };

    const renderStatusLabel = (statusValue) => {
      if (statusValue === 'All Statuses') return t('history:filters.statuses.all');
      // API statuses are left as-is; optionally you could map them.
      return statusValue;
    };

    const renderDatesPlaceholder = () => t('history:filters.datesPlaceholder');

    return (
      <div>
        <Header />
        <div className="transaction-container">
          <div className="spare-dvs vh-100 align-items-start">
            <div className="w-100 float-left">
              <div style={{ position: 'relative', marginBottom: '20px', textAlign: 'center', width: '100%' }}>
                <button onClick={() => this.props.history.push({ pathname: '/dashboard' })}
                  style={{
                    position: 'absolute', left: '0', top: '50%',
                    transform: 'translateY(-50%)', border: 'none',
                    background: 'none', cursor: 'pointer'
                  }}>
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none"
                    xmlns="http://www.w3.org/2000/svg">
                    <path d="M15 19l-7-7 7-7" stroke="#333" strokeWidth="2"
                      strokeLinecap="round" strokeLinejoin="round" />
                    <line x1="8" y1="12" x2="20" y2="12"
                      stroke="#333" strokeWidth="2"
                      strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </button>
                <div className='spare'><h2 style={{ textAlign: "center", margin: "0" }}>{t('history:header.title')}</h2></div>
              </div>

              {/* Filters */}
              <div className="filter-container">
                <div className="filter-items">
                  <select
                    className={`filter-select ${selectedDate !== 'All' ? 'is-active' : ''}`}
                    value={selectedDate}
                    onChange={(e) => this.setState({ selectedDate: e.target.value }, this.filterTransactions)}
                    aria-label={t('history:filters.aria.date')}
                  >
                    <option value="All">{renderDatesPlaceholder()}</option>
                    {sortedAllDates.map(date => (
                      <option key={date} value={date}>{date}</option>
                    ))}
                  </select>
                </div>

                <div className="filter-items">
                  <select
                    className={`filter-select ${selectedStatus !== 'All Statuses' ? 'is-active' : ''}`}
                    value={selectedStatus}
                    onChange={(e) => this.setState({ selectedStatus: e.target.value }, this.filterTransactions)}
                    aria-label={t('history:filters.aria.status')}
                  >
                    {allStatuses.map(status => (
                      <option key={status} value={status}>{renderStatusLabel(status)}</option>
                    ))}
                  </select>
                </div>

                <div className="filter-items">
                  <select
                    className={`filter-select ${selectedType !== 'All Types' ? 'is-active' : ''}`}
                    value={selectedType}
                    onChange={(e) => this.setState({ selectedType: e.target.value }, this.filterTransactions)}
                    aria-label={t('history:filters.aria.type')}
                  >
                    {allTypes.map(type => (
                      <option key={type} value={type}>{renderTypeLabel(type)}</option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Active filter chips */}
              {(selectedDate !== 'All' || selectedStatus !== 'All Statuses' || selectedType !== 'All Types') && (
                <div className="filter-chips" role="status" aria-live="polite">
                  {selectedDate !== 'All' && (
                    <span className="filter-chip" title={`${t('history:chips.datePrefix')} ${selectedDate}`}>
                      {t('history:chips.datePrefix')} {selectedDate}
                      <button className="filter-chip__close" onClick={this.clearDate} aria-label={t('history:chips.clearDate')}>×</button>
                    </span>
                  )}
                  {selectedStatus !== 'All Statuses' && (
                    <span className="filter-chip" title={`${t('history:chips.statusPrefix')} ${renderStatusLabel(selectedStatus)}`}>
                      {t('history:chips.statusPrefix')} {renderStatusLabel(selectedStatus)}
                      <button className="filter-chip__close" onClick={this.clearStatus} aria-label={t('history:chips.clearStatus')}>×</button>
                    </span>
                  )}
                  {selectedType !== 'All Types' && (
                    <span className="filter-chip" title={`${t('history:chips.typePrefix')} ${renderTypeLabel(selectedType)}`}>
                      {t('history:chips.typePrefix')} {renderTypeLabel(selectedType)}
                      <button className="filter-chip__close" onClick={this.clearType} aria-label={t('history:chips.clearType')}>×</button>
                    </span>
                  )}
                  <span className="filter-chip" title={t('history:chips.clearAllTitle')}>
                    {t('history:chips.clearAll')}
                    <button className="filter-chip__close" onClick={this.clearAllFilters} aria-label={t('history:chips.clearAllAria')}>×</button>
                  </span>
                </div>
              )}

              {/* Top Pagination */}
              {total > 0 && (
                <Pagination
                  currentPage={currentPage}
                  totalPages={totalPages}
                  onPageChange={this.goToPage}
                  total={total}
                  startIndex={startIndex}
                  endIndex={endIndex}
                />
              )}

              {/* Page content grouped by date */}
              {Object.entries(pageTransactionsByDate).length > 0 ? (
                Object.entries(pageTransactionsByDate).map(([date, transactions]) => (
                  <div key={date} style={{ marginBottom: "15px" }}>
                    <div className='history-date'>{date}</div>

                    {Array.isArray(transactions) && transactions.map(transaction => (
                      <div key={`${transaction.source_table}-${transaction.cash_request_id ?? transaction.transaction_id ?? transaction.id}`}>
                        <div className='history-container'>

                          <div className='history-left'>
                            {transaction.source_table === 'transactions' && (
                              <>
                                <p style={{ fontWeight: "bold", fontSize: "14px" }}>
                                  {t('history:types.withdrawal')}
                                </p>
                                <p style={{ fontSize: "14px" }}>
                                  {transaction.method || t('history:agents.unknownAgent')}
                                </p>
                              </>
                            )}

                            {transaction.source_table === 'paypal_transaction' && (
                              <>
                                <p style={{ fontWeight: "bold", color: "#22212a", fontSize: "14px" }}>
                                  {t('history:types.deposit')}
                                </p>
                                <p style={{ color: "#747e90", fontSize: "14px" }}>
                                  {transaction.method || 'PayPal'}
                                </p>
                              </>
                            )}

                            {transaction.source_table === 'user_account_history' && (
                              <>
                                <p style={{ fontWeight: "bold", color: "#22212a", fontSize: "14px" }}>
                                  {t('history:types.deposit')}
                                </p>
                                <p style={{ color: "#747e90", fontSize: "14px" }}>
                                  {transaction.method || t('history:agents.unknown')}
                                </p>
                              </>
                            )}
                          </div>

                          <div className='history-right'>
                            {transaction.source_table === 'transactions' && (
                              <div>
                                <p style={{ padding: "5px" }}>{t('history:labels.amount')}: ${transaction.amount}</p>
                                <p style={{ padding: "5px" }}>{t('history:labels.fee')}: ${transaction.fee}</p>
                                <p style={{
                                  color: this.determineColor(transaction.status),
                                  backgroundColor: this.determineBack(transaction.status),
                                  padding: "5px 10px",
                                  borderRadius: "55px"
                                }}>
                                  {transaction.status}
                                </p>
                              </div>
                            )}

                            {transaction.source_table === 'paypal_transaction' && (
                              <div>
                                <p style={{ padding: "5px" }}>{t('history:labels.amount')}: ${(transaction.amount / 100).toFixed(2)}</p>
                                <p style={{ padding: "5px" }}>{t('history:labels.fee')}: ${(transaction.fee / 100).toFixed(2)}</p>
                                <p style={{
                                  color: this.determineColor(transaction.status),
                                  backgroundColor: this.determineBack(transaction.status),
                                  padding: "5px 10px",
                                  borderRadius: "55px"
                                }}>
                                  {transaction.status}
                                </p>
                              </div>
                            )}

                            {transaction.source_table === 'user_account_history' && (
                              <div>
                                <p style={{ padding: "5px" }}>{t('history:labels.amount')}: ${transaction.amount}</p>
                                <p style={{ padding: "5px" }}>{t('history:labels.fee')}: ${transaction.fee}</p>
                                <p style={{
                                  color: this.determineColor(transaction.status),
                                  backgroundColor: this.determineBack(transaction.status),
                                  padding: "5px 10px",
                                  borderRadius: "55px"
                                }}>
                                  {transaction.status}
                                </p>
                              </div>
                            )}
                          </div>

                        </div>
                      </div>
                    ))}
                  </div>
                ))
              ) : (
                <p>{t('history:noRecords')}</p>
              )}

            </div>
          </div>
        </div>
      </div>
    );
  }
}

const mapDispatchToProps = dispatch =>
  bindActionCreators({ tokenAction: tokenAction }, dispatch);

const mapStateToProps = state => ({
  token: state.token,
  profile: state.profile
});

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(
  withRouter(
    withRecoil(
      withTranslation(['history'])(History),
      [accessTokenState]
    )
  )
);
